import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Copy, DatabaseBackup, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TONE = {
  ok: ["bg-lime", "Aman", "border-lime/30"],
  warn: ["bg-volt", "Perlu dicek", "border-volt/40"],
  bad: ["bg-red-500", "Perlu update", "border-red-500/40"],
  unknown: ["bg-zinc-500", "Tidak tersedia", "border-white/10"],
};

const Check = ({ c }) => {
  const [dot, label, border] = TONE[c.status] || TONE.unknown;
  return (
    <div data-testid={`health-check-${c.key}`} className={`rounded-2xl border ${border} bg-ink p-5`}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-display text-paper">{c.name}</span>
        <span className="flex items-center gap-2 text-xs text-zinc-300"><span className={`h-2.5 w-2.5 rounded-full ${dot}`} />{label}</span>
      </div>
      <p className="mt-1 font-mono text-xs text-zinc-400">{c.current}</p>
      <p data-testid={`health-message-${c.key}`} className="mt-3 text-sm leading-relaxed text-zinc-300">{c.message}</p>
    </div>
  );
};

const summary = (data) =>
  `Info kesehatan server BagiPulsa (${new Date(data.checked_at).toLocaleString("id-ID")}):\n` +
  data.checks.map((c) => `- ${c.name}: ${c.current} — ${TONE[c.status]?.[1]}. ${c.message}`).join("\n") +
  "\nTolong bantu langkah update-nya.";

const download = async (fname) => {
  try {
    const res = await api.get(`/admin/backups/${fname}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: fname });
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Gagal download backup");
  }
};

export const HealthPanel = () => {
  const [data, setData] = useState(null);
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setData(null);
    api.get("/admin/health").then((r) => setData(r.data)).catch(() => setData({ checks: [], checked_at: new Date().toISOString() }));
    api.get("/admin/backups").then((r) => setBackups(Array.isArray(r.data) ? r.data : [])).catch(() => setBackups([]));
  };
  useEffect(() => {
    load();
  }, []);
  const backupNow = async () => {
    setBusy(true);
    try {
      await api.post("/admin/backups");
      toast.success("Backup selesai");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const issues = (data?.checks || []).filter((c) => c.status === "bad" || c.status === "warn").length;
  return (
    <section data-testid="admin-health-panel" className="mt-8 rounded-3xl bg-surface p-6 ring-1 ring-white/[0.07] md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p data-testid="health-summary" className="text-sm text-zinc-300">
          {data ? (issues ? <span className="text-volt">{issues} hal perlu perhatian</span> : "Semua aman") : "Mengecek server…"}
        </p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="health-refresh" onClick={load} className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm text-zinc-200 hover:border-volt"><RefreshCw className="h-4 w-4" /> Cek ulang</button>
          <button data-testid="health-copy" disabled={!data} onClick={() => navigator.clipboard?.writeText(summary(data)).then(() => toast.success("Info disalin. Tempel ke chat Emergent untuk minta bantuan."))} className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm text-zinc-200 hover:border-volt"><Copy className="h-4 w-4" /> Salin info</button>
          <button data-testid="health-backup-now" disabled={busy} onClick={backupNow} className="flex items-center gap-1.5 rounded-full bg-volt px-4 py-2 text-sm font-semibold text-ink disabled:opacity-60"><DatabaseBackup className="h-4 w-4" /> {busy ? "Membackup…" : "Backup sekarang"}</button>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
        {(data?.checks || []).map((c) => <Check key={c.key} c={c} />)}
      </div>
      {backups.length ? (
        <div className="mt-6" data-testid="health-backup-list">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Backup tersimpan (10 terakhir)</p>
          {backups.map((b) => (
            <div key={b.filename} className="flex items-center justify-between gap-3 border-b border-white/[0.07] py-3 text-sm">
              <span className="truncate text-zinc-300">{new Date(b.created_at).toLocaleString("id-ID")} · {b.size_kb} KB</span>
              <button data-testid={`health-backup-download-${b.filename}`} onClick={() => download(b.filename)} className="flex shrink-0 items-center gap-1 text-volt hover:underline"><Download className="h-4 w-4" /> Download</button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};
